import { useAuthentication } from '@src/authentication/useAuthentication';
import type { Client } from '@src/clients/domain/Client';
import { isClientUser } from '@src/clientUser/useClientUser';
import { LoadingPage } from '@src/common/components/LoadingPage';
import type { Order } from '@src/orders/domain/Order';
import { OrderActions } from '@src/orders/domain/OrderList';
import type { EditOrderProps } from '@src/orders/EditOrder';
import { useClient } from '@src/orders/hooks/useClient';
import { useOrder } from '@src/orders/hooks/useOrder';
import { useOrderValuesContext } from '@src/orders/hooks/useOrderValuesContext';
import { ViewOrderProvider } from '@src/orders/ViewOrderProvider';
import { useTractors } from '@src/tractor/hooks/useTractors';
import { useTrailers } from '@src/trailer/hooks/useTrailers';
import { useTranslation } from 'react-i18next';

export type EditOrderPage = (
  props: Omit<EditOrderProps, 'ActionsFooter' | 'onSubmit'>,
) => JSX.Element;

interface Props {
  orderId: string;
  EditOrderPage: EditOrderPage;
  disabledFields?: string[];
}

export const EditOrderProvider = ({
  orderId,
  EditOrderPage,
  disabledFields = [],
}: Props) => {
  const { t } = useTranslation();
  const { isLoading, order } = useOrder({ id: orderId });
  const { user } = useAuthentication();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!order) {
    return <>{t('loadingError')}</>;
  }
  if (isClientUser(user) && !order.canDo(OrderActions.EDIT)) {
    return <ViewOrderProvider orderId={order.id} />;
  }
  return (
    <LoadedOrder
      order={order}
      EditOrderPage={EditOrderPage}
      disabledFields={disabledFields}
    />
  );
};

const LoadedOrder = ({
  order,
  EditOrderPage,
  disabledFields,
}: {
  order: Order;
  EditOrderPage: EditOrderPage;
  disabledFields: string[];
}) => {
  const { t } = useTranslation();
  const { isLoading: isClientLoading, client } = useClient({
    id: order.clientId,
  });

  if (isClientLoading) {
    return <LoadingPage />;
  }

  if (!client) {
    return <>{t('loadingError')}</>;
  }

  return (
    <LoadedClientAndOrder
      order={order}
      client={client}
      EditOrderPage={EditOrderPage}
      disabledFields={disabledFields}
    />
  );
};

const LoadedClientAndOrder = ({
  order,
  client,
  EditOrderPage,
  disabledFields,
}: {
  order: Order;
  client: Client;
  EditOrderPage: EditOrderPage;
  disabledFields: string[];
}) => {
  const { t } = useTranslation();
  const { values, setValues } = useOrderValuesContext();

  const { trailers, isLoading: isTrailersLoading } = useTrailers(client.id);
  const { tractors, isLoading: isTractorsLoading } = useTractors(client.id);

  if (isTrailersLoading || isTractorsLoading) {
    return <LoadingPage />;
  }

  if (!trailers || !tractors) {
    return <>{t('loadingError')}</>;
  }

  return (
    <EditOrderPage
      order={order}
      checkedFields={order.getFields()}
      disabledFields={disabledFields}
      permitInstructions={order.getPermitsInstructions()}
      client={client}
      trailers={trailers.trailers}
      tractors={tractors.tractors}
      defaultValues={values}
      onValuesChange={setValues}
    />
  );
};
